import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { confirm } from '@/shared/lib/confirm';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Download,
  Loader2,
  Pause,
  Play,
  Trash2,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  type RecipientRow,
  type RecipientSortColumn,
  deleteCampaign,
  getCampaign,
  listRecipients,
  pauseCampaign,
  resumeCampaign,
} from './api';
import { num } from './campaign-ui';

const STATUS_COLORS: Record<RecipientRow['status'], string> = {
  pending: 'bg-n-100 text-n-700',
  sent: 'bg-brand-50 text-brand-700',
  delivered: 'bg-success-50 text-success-700',
  read: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-danger-50 text-danger-600',
};

const STATUS_LABELS: Record<RecipientRow['status'], string> = {
  pending: 'Pendente',
  sent: 'Enviada',
  delivered: 'Entregue',
  read: 'Lida',
  failed: 'Falhou',
};

export function CampanhaDetailPage() {
  // biome-ignore lint/suspicious/noExplicitAny: dynamic param
  const params = useParams({ strict: false }) as any;
  const id = Number(params?.id);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<RecipientRow['status'] | ''>('');
  // Ordenação por coluna (null = ordem de inserção). Clicar no header alterna
  // a direção; clicar em outra coluna recomeça em ascendente.
  const [sort, setSort] = useState<{ col: RecipientSortColumn; dir: 'asc' | 'desc' } | null>(null);
  const toggleSort = (col: RecipientSortColumn): void =>
    setSort((prev) =>
      prev?.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' },
    );
  const [exporting, setExporting] = useState(false);

  const detailQ = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => getCampaign(id),
    enabled: Number.isFinite(id),
    // Atualiza a barra + contadores (inclui delivered/read que chegam por
    // webhook após o envio) sem F5. `inBackground` cobre aba/janela sem foco.
    refetchInterval: 3000,
    refetchIntervalInBackground: true,
  });
  const recipientsQ = useInfiniteQuery({
    queryKey: ['campaign-recipients', id, statusFilter, sort?.col ?? '', sort?.dir ?? ''],
    queryFn: ({ pageParam }) =>
      listRecipients(id, {
        ...(statusFilter && { status: statusFilter }),
        limit: 100,
        offset: pageParam,
        ...(sort && { sort: sort.col, dir: sort.dir }),
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.next_offset ?? undefined,
    enabled: Number.isFinite(id),
    placeholderData: (prev) => prev,
    gcTime: 0,
  });

  const pauseMut = useMutation({
    mutationFn: () => pauseCampaign(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaign', id] }),
  });
  const resumeMut = useMutation({
    mutationFn: () => resumeCampaign(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaign', id] }),
  });
  const deleteMut = useMutation({
    mutationFn: () => deleteCampaign(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      // biome-ignore lint/suspicious/noExplicitAny: dynamic route
      void navigate({ to: '/campanhas' as any });
    },
  });

  const recipients = recipientsQ.data?.pages.flatMap((p) => p.recipients) ?? [];
  const recipientsTotal = recipientsQ.data?.pages[0]?.total ?? 0;

  // Baixa a lista INTEIRA em CSV (não só a página visível): pagina o endpoint
  // até o fim, respeitando o filtro de status e a ordenação atuais.
  const downloadCsv = async (): Promise<void> => {
    setExporting(true);
    try {
      const all: RecipientRow[] = [];
      let offset = 0;
      // Loop de páginas — limite alto (500) reduz o nº de requests.
      for (;;) {
        const page = await listRecipients(id, {
          ...(statusFilter && { status: statusFilter }),
          ...(sort && { sort: sort.col, dir: sort.dir }),
          limit: 500,
          offset,
        });
        all.push(...page.recipients);
        if (page.next_offset == null) break;
        offset = page.next_offset;
      }
      const esc = (v: string | null | undefined): string =>
        `"${String(v ?? '').replace(/"/g, '""')}"`;
      const fmt = (iso: string | null): string =>
        iso ? new Date(iso).toLocaleString('pt-BR') : '';
      const header = ['Contato', 'Telefone', 'Status', 'Enviado', 'Entregue', 'Lido', 'Erro'];
      const lines = [header.map(esc).join(',')];
      for (const r of all) {
        lines.push(
          [
            r.contact_name ?? `#${r.contact_id}`,
            r.phone ?? '',
            STATUS_LABELS[r.status],
            fmt(r.attempted_at),
            fmt(r.delivered_at),
            fmt(r.read_at),
            r.error_code ? `${r.error_code}: ${r.error_message ?? ''}` : '',
          ]
            .map(esc)
            .join(','),
        );
      }
      // BOM (﻿) pro Excel ler acentos em UTF-8.
      const blob = new Blob([`﻿${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const slug = (detailQ.data?.name ?? `campanha-${id}`)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      a.download = `${slug || `campanha-${id}`}-destinatarios.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  // IntersectionObserver no final da lista — idêntico a Contatos.
  const sentinelRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !recipientsQ.hasNextPage || recipientsQ.isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) void recipientsQ.fetchNextPage();
        }
      },
      { rootMargin: '200px', threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [recipientsQ.hasNextPage, recipientsQ.isFetchingNextPage, recipientsQ.fetchNextPage]);

  if (detailQ.isLoading || !detailQ.data) {
    return (
      <div className="flex h-full flex-1 items-center justify-center">
        <Loader2 className="animate-spin text-n-400" />
      </div>
    );
  }

  const c = detailQ.data;
  const counts = c.counts;
  const sent = (counts.sent ?? 0) + (counts.delivered ?? 0) + (counts.read ?? 0);
  const failed = counts.failed ?? 0;
  // Progresso = processado (enviado + falhou) / total → 100% quando o disparo
  // termina, mesmo com falhas. A falha aparece como segmento vermelho na barra.
  const successPct = c.total_count > 0 ? Math.round((sent / c.total_count) * 100) : 0;
  const failedPct = c.total_count > 0 ? Math.round((failed / c.total_count) * 100) : 0;
  const processedPct = Math.min(100, successPct + failedPct);

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden bg-n-50">
      {/* Header fixo */}
      <header className="flex flex-shrink-0 items-center gap-3 border-b border-n-200 bg-n-0 px-5 py-4">
        <Link
          // biome-ignore lint/suspicious/noExplicitAny: dynamic
          to={'/campanhas' as any}
          className="flex h-8 w-8 items-center justify-center rounded text-n-600 hover:bg-n-100"
        >
          <ArrowLeft size={16} />
        </Link>
        <div className="min-w-0 flex-1">
          <h1
            className="truncate font-bold text-[20px] text-n-900"
            style={{ letterSpacing: '-.02em' }}
          >
            {c.name}
          </h1>
          <span className="text-[12px] text-n-500">
            {c.status} · iniciada{' '}
            {c.started_at ? new Date(c.started_at).toLocaleString('pt-BR') : '—'}
          </span>
        </div>
        {c.status === 'running' && (
          <button
            type="button"
            onClick={() => pauseMut.mutate()}
            className="flex items-center gap-1.5 rounded border border-n-200 bg-n-0 px-3 py-2 font-semibold text-[12px] hover:bg-n-50"
          >
            <Pause size={14} />
            Pausar
          </button>
        )}
        {(c.status === 'paused' || c.status === 'scheduled') && (
          <button
            type="button"
            onClick={() => resumeMut.mutate()}
            className="flex items-center gap-1.5 rounded bg-brand-500 px-3 py-2 font-semibold text-[12px] text-white"
          >
            <Play size={14} />
            {c.status === 'scheduled' ? 'Iniciar agora' : 'Retomar'}
          </button>
        )}
        {/* Excluir — disponível em qualquer status (remove de vez) */}
        <button
          type="button"
          onClick={async () => {
            const ok = await confirm({
              title: 'Excluir campanha?',
              description: `A campanha "${c.name}" será removida permanentemente.`,
              confirmText: 'Excluir',
              cancelText: 'Voltar',
              variant: 'danger',
            });
            if (ok) deleteMut.mutate();
          }}
          className="flex items-center gap-1.5 rounded bg-danger-500 px-3 py-2 font-semibold text-[12px] text-white"
        >
          <Trash2 size={14} />
          Excluir
        </button>
      </header>

      {/* Progresso + filtro — fixos */}
      <div className="flex-shrink-0 space-y-3 px-5 pt-5">
        <div className="rounded-md border border-n-200 bg-n-0 p-4 shadow-sm">
          <div className="flex items-center justify-between text-[12px]">
            <span className="font-semibold text-n-700">Progresso</span>
            <span className="font-mono">
              {num(sent + failed)} / {num(c.total_count)} ({processedPct}%)
            </span>
          </div>
          <div className="mt-2 flex h-2 overflow-hidden rounded-full bg-n-100">
            <div
              className="h-full bg-brand-500 transition-all"
              style={{ width: `${successPct}%` }}
            />
            <div
              className="h-full bg-danger-500 transition-all"
              style={{ width: `${failedPct}%` }}
            />
          </div>
          <div className="mt-3 grid grid-cols-5 gap-3 text-[12px]">
            <Stat label="Pendentes" value={counts.pending ?? 0} color="text-n-700" />
            <Stat label="Enviadas" value={counts.sent ?? 0} color="text-brand-700" />
            <Stat label="Entregues" value={counts.delivered ?? 0} color="text-success-700" />
            <Stat label="Lidas" value={counts.read ?? 0} color="text-emerald-700" />
            <Stat label="Falhas" value={counts.failed ?? 0} color="text-danger-600" />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[12px] text-n-600">Filtrar:</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter((e.target.value as RecipientRow['status'] | '') || '')}
            className="h-9 rounded border border-n-200 bg-n-0 px-2 text-[12px]"
          >
            <option value="">Todos</option>
            <option value="pending">Pendentes</option>
            <option value="sent">Enviadas</option>
            <option value="delivered">Entregues</option>
            <option value="read">Lidas</option>
            <option value="failed">Falhas</option>
          </select>
          {recipientsTotal > 0 && (
            <span className="ml-auto text-[12px] text-n-500">
              {recipients.length >= recipientsTotal
                ? `${num(recipientsTotal)} destinatário(s)`
                : `Mostrando ${num(recipients.length)} de ${num(recipientsTotal)}`}
            </span>
          )}
          {recipientsTotal > 0 && (
            <button
              type="button"
              onClick={() => void downloadCsv()}
              disabled={exporting}
              title="Baixar a lista (CSV) com o status de cada número"
              className="flex h-9 items-center gap-1.5 rounded border border-n-200 bg-n-0 px-3 text-[12px] font-semibold text-n-700 hover:bg-n-50 disabled:opacity-60"
            >
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {exporting ? 'Gerando…' : 'Baixar CSV'}
            </button>
          )}
        </div>
      </div>

      {/* Tabela de destinatários — scroll DENTRO do card (igual Contatos) */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-5">
        <div className="flex min-h-0 flex-col overflow-hidden rounded-md border border-n-200 bg-n-0 shadow-sm">
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="w-full min-w-[760px] text-[12px]">
              <thead className="sticky top-0 z-10 border-b border-n-200 bg-n-50 text-left">
                <tr>
                  <Th sortKey="contact" sort={sort} onSort={toggleSort}>
                    Contato
                  </Th>
                  <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase tracking-[.05em] text-n-500">
                    Telefone
                  </th>
                  <Th sortKey="status" sort={sort} onSort={toggleSort}>
                    Status
                  </Th>
                  <Th sortKey="attempted" sort={sort} onSort={toggleSort}>
                    Enviado
                  </Th>
                  <Th sortKey="delivered" sort={sort} onSort={toggleSort}>
                    Entregue
                  </Th>
                  <Th sortKey="read" sort={sort} onSort={toggleSort}>
                    Lido
                  </Th>
                  <Th sortKey="error" sort={sort} onSort={toggleSort}>
                    Erro
                  </Th>
                </tr>
              </thead>
              <tbody>
                {recipientsQ.isLoading && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center">
                      <Loader2 size={20} className="mx-auto animate-spin text-n-400" />
                    </td>
                  </tr>
                )}
                {!recipientsQ.isLoading && recipients.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-10 text-center text-n-500">
                      Nenhum destinatário {statusFilter && 'com esse status'}.
                    </td>
                  </tr>
                )}
                {recipients.map((r) => (
                  <tr key={r.id} className="border-b border-n-100 last:border-b-0 hover:bg-n-50">
                    <td className="px-[14px] py-[11px] text-n-800">
                      {r.contact_name ?? `#${r.contact_id}`}
                    </td>
                    <td className="px-[14px] py-[11px] text-n-600 tabular-nums">
                      {r.phone ?? '—'}
                    </td>
                    <td className="px-[14px] py-[11px]">
                      <span
                        className={`rounded-pill px-2 py-[2px] font-bold text-[10px] uppercase ${STATUS_COLORS[r.status]}`}
                      >
                        {STATUS_LABELS[r.status]}
                      </span>
                    </td>
                    <td className="px-[14px] py-[11px] text-n-600">
                      {r.attempted_at ? new Date(r.attempted_at).toLocaleString('pt-BR') : '—'}
                    </td>
                    <td className="px-[14px] py-[11px] text-n-600">
                      {r.delivered_at ? new Date(r.delivered_at).toLocaleTimeString('pt-BR') : '—'}
                    </td>
                    <td className="px-[14px] py-[11px] text-n-600">
                      {r.read_at ? new Date(r.read_at).toLocaleTimeString('pt-BR') : '—'}
                    </td>
                    <td
                      className="px-[14px] py-[11px] text-[11px] text-danger-600"
                      title={r.error_message ?? undefined}
                    >
                      {r.error_code ?? '—'}
                    </td>
                  </tr>
                ))}
                {recipientsQ.hasNextPage && (
                  <tr ref={sentinelRef}>
                    <td colSpan={7} className="py-4 text-center text-[12px] text-n-500">
                      {recipientsQ.isFetchingNextPage ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 size={14} className="animate-spin" /> Carregando mais…
                        </span>
                      ) : (
                        'Role para carregar mais'
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Th({
  children,
  sortKey,
  sort,
  onSort,
}: {
  children: React.ReactNode;
  sortKey: RecipientSortColumn;
  sort: { col: RecipientSortColumn; dir: 'asc' | 'desc' } | null;
  onSort: (col: RecipientSortColumn) => void;
}) {
  const active = sort?.col === sortKey;
  return (
    <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase tracking-[.05em] text-n-500">
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`flex items-center gap-1 uppercase hover:text-n-800 ${active ? 'text-n-800' : ''}`}
      >
        {children}
        {active ? (
          sort?.dir === 'asc' ? (
            <ChevronUp size={13} />
          ) : (
            <ChevronDown size={13} />
          )
        ) : (
          <ChevronsUpDown size={13} className="text-n-400" />
        )}
      </button>
    </th>
  );
}

function Stat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div>
      <span className={`block font-bold text-[18px] ${color}`}>{num(value)}</span>
      <span className="block text-[10px] text-n-500 uppercase">{label}</span>
    </div>
  );
}
